import type {
  BackEase,
  CustomBounceEase,
  CustomEase,
  CustomWiggleEase,
  ElasticEase,
  ExpoScaleEase,
  RoughEase,
  SlowMoEase,
  StandardPlaybackControl,
  StepsEase,
} from '@packages/systems/ix3/schema';

/**
 * Runtime-Safe Constants for IX3 Core
 *
 * This file contains const enums and constants that must be available at runtime
 * without importing Zod or any schema validation libraries. These constants are
 * duplicated from the schema package to ensure bundle safety in published sites.
 *
 * IMPORTANT: These values must stay in sync with the schema definitions.
 * Any changes should be made in both places.
 */

/**
 * Defines how a timeline's playback is controlled
 * Determines the mechanism that drives the animation forward
 */
export const enum TimelineControlType {
  /** Standard play/pause/reverse controls triggered by events */
  STANDARD = 'standard',
  /** ScrollTrigger-based timeline control that syncs with scroll position */
  SCROLL = 'scroll',
  /** Timeline control that automatically plays on load */
  LOAD = 'load',
  /** Continuous scrubbing control for mouse move and similar triggers */
  CONTINUOUS = 'continuous',
}

/**
 * Type guard to check if a string is a valid TimelineControlType.
 * Used to safely narrow string literals from schema to the enum type.
 */
export function isValidControlType(
  value: string
): value is TimelineControlType {
  return (
    value === TimelineControlType.STANDARD ||
    value === TimelineControlType.SCROLL ||
    value === TimelineControlType.LOAD ||
    value === TimelineControlType.CONTINUOUS
  );
}

/**
 * Type of tween to perform, represented as a number enum for bundle efficiency
 */
export const enum TweenType {
  To = 0,
  From = 1,
  FromTo = 2,
  Set = 3,
}

export type TweenTypeName = 'to' | 'from' | 'both' | 'set';

export function tweenTypeFromName(name: TweenTypeName): TweenType {
  switch (name) {
    case 'to':
      return TweenType.To;
    case 'from':
      return TweenType.From;
    case 'both':
      return TweenType.FromTo;
    case 'set':
      return TweenType.Set;
  }
}

export function tweenTypeToName(
  value: TweenType | undefined
): TweenTypeName | null {
  switch (value) {
    case TweenType.To:
      return 'to';
    case TweenType.From:
      return 'from';
    case TweenType.FromTo:
      return 'both';
    case TweenType.Set:
      return 'set';
    default:
      return null;
  }
}

/**
 * Core logical operators for conditional logic
 */
export const enum CORE_OPERATORS {
  AND = 'wf:and',
  OR = 'wf:or',
}

/**
 * Default values used throughout the IX3 system
 * These provide consistent fallback values when properties are undefined
 */
export const enum DEFAULTS {
  /** Default duration for animations when no duration is specified (in seconds) */
  DURATION = 0.5,
}

/**
 * Nominal length (in seconds) that a percent-canvas timeline's 0–100% maps to.
 *
 * Scroll-scrub and mouse-move timelines aren't time-based — their progress is
 * driven by the gesture — so the editor presents duration/start as percent
 * (0–100%) over a fixed canvas instead of arbitrary seconds. Durations are
 * still stored in seconds; this is the divisor that ties the two together:
 * 1s ⇒ 100%, so the default 0.5s action reads as 50% and 1% maps to 10ms (the
 * timeline snap interval). Stored as `timeline.canvasDuration` when a timeline
 * opts into the percent canvas (see the IX3 timeline schema).
 */
export const PERCENT_CANVAS_DURATION_S = 1;

export const enum RELATIONSHIP_TYPES {
  NONE = 'none',
  WITHIN = 'within',
  DIRECT_CHILD_OF = 'direct-child-of',
  CONTAINS = 'contains',
  DIRECT_PARENT_OF = 'direct-parent-of',
  NEXT_TO = 'next-to',
  NEXT_SIBLING_OF = 'next-sibling-of',
  PREV_SIBLING_OF = 'prev-sibling-of',
}

/**
 * Default parameter values for advanced ease types.
 * These defaults match GSAP's built-in defaults where possible and provide
 * sensible starting points when users select "Custom" eases.
 */
export const EASE_DEFAULTS = {
  back: {
    type: 'back' as const,
    curve: 'out' as const,
    power: 1.7,
  } satisfies BackEase,

  elastic: {
    type: 'elastic' as const,
    curve: 'out' as const,
    amplitude: 1,
    period: 0.3,
  } satisfies ElasticEase,

  steps: {
    type: 'steps' as const,
    stepCount: 6,
  } satisfies StepsEase,

  rough: {
    type: 'rough' as const,
    templateCurve: 'none.inOut' as const,
    points: 20,
    strength: 1,
    taper: 'none' as const,
    randomizePoints: true,
    clampPoints: false,
  } satisfies RoughEase,

  slowMo: {
    type: 'slowMo' as const,
    linearRatio: 0.7,
    power: 0.7,
    yoyoMode: false,
  } satisfies SlowMoEase,

  expoScale: {
    type: 'expoScale' as const,
    startingScale: 0.05,
    endingScale: 1,
    templateCurve: 'none.inOut' as const,
  } satisfies ExpoScaleEase,

  customWiggle: {
    type: 'customWiggle' as const,
    wiggles: 10,
    wiggleType: 'easeOut' as const,
  } satisfies CustomWiggleEase,

  customBounce: {
    type: 'customBounce' as const,
    strength: 0.7,
    squash: 1,
    endAtStart: false,
  } satisfies CustomBounceEase,

  customEase: {
    type: 'customEase' as const,
    // Unique S-curve with subtle hesitation - starts slow, pauses mid-way, then accelerates
    // Properly oriented: starts at (0,160) = progress 0, ends at (160,0) = progress 1
    bezierCurve: 'M0,160 C40,160 24,96 80,96 136,96 120,0 160,0',
  } satisfies CustomEase,
} as const;

/**
 * Default ease used when initializing custom eased animations.
 * Using back.out as it provides a visually appealing starting point.
 */
export const DEFAULT_CUSTOM_EASE: BackEase = EASE_DEFAULTS.back;

/**
 * Full set of `StandardPlaybackControl` values in the preferred dropdown order
 * for trigger-popover Control selectors. Consumers pass this to
 * `IX3PlaybackSettings`' `allowedControls` prop to explicitly opt in to the
 * complete set — including `reverseFlipEase`, which is otherwise filtered
 * out of the default options as a guard against stale-CDN GSAP versions that
 * silently ignore the `easeReverse` tween var (see #105465).
 */
export const STANDARD_TRIGGER_ALLOWED_CONTROLS: readonly StandardPlaybackControl[] =
  [
    'restart',
    'play',
    'reverse',
    'reverseFlipEase',
    'pause',
    'resume',
    'togglePlayReverse',
    'togglePlayReverseFlipEase',
    'stop',
    'none',
  ];
